import { pubText, pubPath, pubRich } from "../i18n/publicTranslation";
import { stripLanguage } from '../i18n/languages';
import { useLanguage } from '../contexts/LanguageContext';
import AppDialog, { ModalButton } from './common/AppDialog';
import React, { useState, useEffect, useCallback, useRef, useLayoutEffect, useMemo } from 'react';
import { Box, Button, ButtonBase, Typography, IconButton, Drawer, List, ListItem, ListItemButton, ListItemText, ListItemIcon, useMediaQuery } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HomeIcon from '@mui/icons-material/Home';
import LogoutIcon from '@mui/icons-material/Logout';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import GroupsIcon from '@mui/icons-material/Groups';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import ShareIcon from '@mui/icons-material/Share';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import DangerousIcon from '@mui/icons-material/Dangerous';
import { useNavigate, useLocation } from 'react-router-dom';
import './Header.css';
import { getAllRoutePaths } from '../util/routeConfig';
import LoginButton from './LoginButton';
import { useAppSelector } from '../store/hooks';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureGates } from '../hooks/useFeatureGates';
import { formatTrialOfferCountdown, formatTrialUnlockPrice, getTrialOfferRemainingMs } from '../util/trialOffer';
import DashboardHeader from './DashboardHeader';
import { isDashboardPath } from '../util/dashboardRoutes';
import { WEB_RELEASE_FLAGS } from '../config/releaseFlags';

// Import images
const coinImage = '/coin.png';
const fableIcon = '/fable-butterfly-gold.png?v=butterfly-20260916';

// Public navigation items. The Fable brand owns the Home destination.
const navItems = [
  { label: 'Gambit', path: '/gambit', activeColor: '#C4A0F5' },
  { label: 'Narrators', path: '/narrators', activeColor: '#FF8292' }, // rose, matching narrator text
  { label: 'Musicals', path: '/musicals', activeColor: '#C084FC' }, // violet, matching musical text
  { label: 'Custom Books', path: '/custom-books', activeColor: '#F4C985' }, // warm gold
  { label: 'Stories', path: '/stories', activeColor: '#60A5FA' }, // bright blue
  { label: 'Styles', path: '/styles', activeColor: '#FDE047' }, // bright yellow
  { label: 'Languages', path: '/languages', activeColor: '#5EEAD4' }, // bright teal
  { label: 'Morals', path: '/morals', activeColor: '#F2CD72' }, // crystal gold, matching morals text
  { label: 'Blog', path: '/blog', activeColor: '#4ADE80' }, // bright green
  { label: 'Pricing', path: '/payment', activeColor: '#93C5FD' }, // light blue
];

// Drawer nav items - reordered with Pricing at the end
const drawerNavItems = [
  { label: 'Home', path: '/', activeColor: '#D8A363' }, // gold
  { label: 'Gambit', path: '/gambit', activeColor: '#C4A0F5' },
  { label: 'Narrators', path: '/narrators', activeColor: '#FF8292' }, // rose, matching narrator text
  { label: 'Musicals', path: '/musicals', activeColor: '#C084FC' }, // violet, matching musical text
  { label: 'Custom Books', path: '/custom-books', activeColor: '#F4C985' }, // warm gold
  { label: 'Stories', path: '/stories', activeColor: '#60A5FA' }, // bright blue
  { label: 'Styles', path: '/styles', activeColor: '#FDE047' }, // bright yellow
  { label: 'Languages', path: '/languages', activeColor: '#5EEAD4' }, // bright teal
  { label: 'Morals', path: '/morals', activeColor: '#F2CD72' }, // crystal gold, matching morals text
  { label: 'Blog', path: '/blog', activeColor: '#4ADE80' }, // bright green
  { label: 'Pricing', path: '/payment', activeColor: '#93C5FD' }, // light blue
];

// Dashboard navigation items — base tabs
const baseDashboardNavItems = [
  { label: 'My Stories', path: '/my-stories', activeColor: '#D8A363' },
  { label: 'Create', path: '/create', activeColor: '#D8A363' },
  { label: 'Account', path: '/settings', activeColor: '#D8A363' },
];

// Extra tabs that only appear when their page is active (inserted after Account)
// Use blue to distinguish from the parent "Account" tab which stays gold
const contextualTabs: Record<string, { label: string; path: string; activeColor: string }> = {
  '/book-builder': { label: 'Order Book', path: '/book-builder', activeColor: '#76A9E7' },
  '/my-orders': { label: 'My Orders', path: '/my-orders', activeColor: '#76A9E7' },
  '/notifications': { label: 'Notifications', path: '/notifications', activeColor: '#76A9E7' },
  '/social-accounts': { label: 'Connected Accounts', path: '/social-accounts', activeColor: '#76A9E7' },
  ...(WEB_RELEASE_FLAGS.socialAnalyticsUiEnabled ? {
    '/social-accounts/analytics': { label: 'Analytics', path: '/social-accounts/analytics', activeColor: '#76A9E7' },
  } : {}),
  '/characters': { label: 'Characters', path: '/characters', activeColor: '#76A9E7' },
  '/voices': { label: 'Voices', path: '/voices', activeColor: '#76A9E7' },
  '/share': { label: 'Share', path: '/share', activeColor: '#76A9E7' },
  '/download': { label: 'Download', path: '/download', activeColor: '#76A9E7' },
  '/support': { label: 'Support', path: '/support', activeColor: '#76A9E7' },
};

const getDashboardContextualTab = (pathname: string) => {
  if (pathname.startsWith('/characters')) {
    return contextualTabs['/characters'];
  }
  if (pathname.startsWith('/voices')) {
    return contextualTabs['/voices'];
  }
  if (pathname.startsWith('/book-builder')) {
    return contextualTabs['/book-builder'];
  }
  return contextualTabs[pathname];
};

interface DashboardDrawerItem {
  icon: React.ReactNode;
  label: string;
  path?: string;
  externalUrl?: string;
  destructive?: boolean;
}

interface DashboardDrawerSection {
  title: string;
  items: DashboardDrawerItem[];
}

// Dashboard drawer items — shown in mobile drawer when authenticated on dashboard pages
// Icons use color="inherit" so the parent ListItemIcon can control the color
const dashboardDrawerSections: DashboardDrawerSection[] = [
  {
    title: 'Membership',
    items: [
      { icon: <img src="/crown.png" alt={pubText("Crown")} style={{ width: 24, height: 24 }} />, label: 'Subscription', path: '/manage-subscription' },
      { icon: <img src="/coin.png" alt={pubText("Tokens")} style={{ width: 24, height: 24 }} />, label: 'Purchase Story Tokens', path: '/buy-tokens' },
    ],
  },
  {
    title: 'Library',
    items: [
      { icon: <AutoStoriesIcon color="inherit" />, label: 'My Stories', path: '/my-stories' },
      { icon: <AddCircleOutlineIcon color="inherit" />, label: 'Create Story', path: '/create' },
      { icon: <GroupsIcon color="inherit" />, label: 'Your Characters', path: '/characters' },
      { icon: <RecordVoiceOverIcon color="inherit" />, label: 'Your Voices', path: '/voices' },
    ],
  },
  {
    title: 'Social Media',
    items: [
      { icon: <ShareIcon color="inherit" />, label: 'Connected Accounts', path: '/social-accounts' },
      ...(WEB_RELEASE_FLAGS.socialAnalyticsUiEnabled ? [
        { icon: <InsightsOutlinedIcon color="inherit" />, label: 'Analytics', path: '/social-accounts/analytics' },
      ] : []),
    ],
  },
  {
    title: 'Books & Orders',
    items: [
      { icon: <MenuBookIcon color="inherit" />, label: 'Create & Order Book', path: '/book-builder' },
      { icon: <LocalShippingIcon color="inherit" />, label: 'My Orders', path: '/my-orders' },
    ],
  },
  {
    title: 'Preferences',
    items: [
      { icon: <NotificationsIcon color="inherit" />, label: 'Notifications', path: '/notifications' },
    ],
  },
  {
    title: 'Help',
    items: [
      { icon: <ContactSupportIcon color="inherit" />, label: 'Support', path: '/support' },
    ],
  },
  {
    title: 'More',
    items: [
      { icon: <DangerousIcon color="inherit" />, label: 'Delete Account', externalUrl: 'https://thefableapp.com/delete-profile', destructive: true },
    ],
  },
];

// Inactive color for nav items
const INACTIVE_COLOR = 'rgba(255, 255, 255, 0.8)';

const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentLanguage } = useLanguage();
  const compactEnglish = useMediaQuery('(max-width:1279.95px)');
  const compactTranslated = useMediaQuery('(max-width:1679.95px)');
  const isMobile = currentLanguage === 'en' ? compactEnglish : compactTranslated;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const { logout } = useAuth();

  // Get user data from Redux store
  const user = useAppSelector((state) => ({
    email: state.app.email,
    userId: state.app.userId,
    tokens: state.app.tokens,
    character_tokens: state.app.character_tokens,
    is_platinum: state.app.is_platinum,
    is_premium: state.app.is_premium,
    is_trial: state.app.is_trial,
    has_used_trial: state.app.has_used_trial,
    trialOffer: state.app.trialOffer,
  }));

  useFeatureGates(user.userId || '', Boolean(user.userId));

  const [trialOfferRemainingMs, setTrialOfferRemainingMs] = useState(0);

  useEffect(() => {
    const updateRemaining = () => {
      setTrialOfferRemainingMs(getTrialOfferRemainingMs(user.trialOffer));
    };

    updateRemaining();
    const interval = window.setInterval(updateRemaining, 1000);
    return () => window.clearInterval(interval);
  }, [user.trialOffer]);

  const canShowSubscriptionCta = user.userId && !user.is_premium && !user.is_platinum && !user.is_trial && !user.has_used_trial;
  const hasActiveTrialOffer = Boolean(canShowSubscriptionCta && user.trialOffer?.enabled === true && trialOfferRemainingMs > 0);
  const trialUnlockPrice = formatTrialUnlockPrice(
    user.trialOffer?.unlockAmountCents,
    user.trialOffer?.unlockCurrency,
  );
  const subscriptionCtaPath = hasActiveTrialOffer
    ? '/payment?category=subscription&trial=true'
    : '/payment?category=subscription';

  const allLandingRoutes = getAllRoutePaths();

  // Scroll progress for blur effect
  const [scrollProgress, setScrollProgress] = useState(0);

  // Refs for animated bubble indicator
  const navContainerRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<(HTMLElement | null)[]>([]);
  const [bubbleStyle, setBubbleStyle] = useState({ left: 0, width: 0 });
  const [bounceKey, setBounceKey] = useState(0);
  const prevActiveIndexRef = useRef(-1);

  // Check if user is on dashboard/app pages (My Library, Settings, Edit Story, etc.)
  const isDashboardPage = isDashboardPath(location.pathname);

  // Build dashboard nav items — append contextual tab after Account if on that page
  const dashboardNavItems = useMemo(() => {
    const extraTab = getDashboardContextualTab(location.pathname);
    if (extraTab) {
      return [...baseDashboardNavItems, extraTab];
    }
    return baseDashboardNavItems;
  }, [location.pathname]);

  // Select which nav items to use based on page type
  const currentNavItems = isDashboardPage ? dashboardNavItems : navItems;

  // Check if current path matches nav item
  const isActive = useCallback((path: string) => {
    const pathname = stripLanguage(location.pathname);
    if (path === '/') {
      return pathname === '/' || allLandingRoutes.includes(pathname);
    }
    if (path === '/narrators') {
      return pathname === '/narrators' || pathname.startsWith('/narrators/');
    }
    if (path === '/stories') {
      return pathname === '/stories' || pathname.startsWith('/story-details');
    }
    if (path === '/musicals') {
      return pathname === '/musicals' || pathname.startsWith('/musicals/');
    }
    if (path === '/my-stories') {
      return pathname === '/my-stories' ||
        pathname.startsWith('/edit-story/') ||
        pathname.startsWith('/story-preview/');
    }
    if (path === '/settings') {
      return pathname === '/settings' || !!getDashboardContextualTab(pathname);
    }
    if (path === '/social-accounts') return pathname === path;
    return pathname === path || pathname.startsWith(path);
  }, [location.pathname, allLandingRoutes]);

  // Find active nav index — prefer the most specific (last) match so the bubble
  // lands on the contextual sub-page tab rather than the parent "Account" tab
  const activeIndex = (() => {
    for (let i = currentNavItems.length - 1; i >= 0; i--) {
      if (isActive(currentNavItems[i].path)) return i;
    }
    return -1;
  })();
  const activeItem = activeIndex >= 0 ? currentNavItems[activeIndex] : null;
  const isBrandActive = stripLanguage(location.pathname) === '/' || allLandingRoutes.includes(stripLanguage(location.pathname));

  // Calculate bubble position when active item changes
  const updateBubblePosition = useCallback(() => {
    if (activeIndex >= 0 && buttonRefs.current[activeIndex] && navContainerRef.current) {
      const button = buttonRefs.current[activeIndex];
      const container = navContainerRef.current;
      if (button && container) {
        const buttonRect = button.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        setBubbleStyle({
          left: buttonRect.left - containerRect.left,
          width: buttonRect.width,
        });
      }
    }
  }, [activeIndex]);

  // Reset button refs when nav items change (switching between dashboard and public nav)
  useEffect(() => {
    buttonRefs.current = [];
    // Reset bubble style to prevent flash of old position
    setBubbleStyle({ left: 0, width: 0 });
  }, [currentNavItems]);

  useLayoutEffect(() => {
    // Use requestAnimationFrame to ensure DOM has updated after nav items change
    const rafId = requestAnimationFrame(() => {
      updateBubblePosition();
    });

    // Trigger bounce animation when active index changes
    if (prevActiveIndexRef.current !== activeIndex && activeIndex >= 0) {
      setBounceKey(prev => prev + 1);
    }
    prevActiveIndexRef.current = activeIndex;

    return () => cancelAnimationFrame(rafId);
  }, [activeIndex, updateBubblePosition, currentNavItems]);

  // Keep the bubble aligned when the window or flex layout changes (for example,
  // after the authentication actions finish loading and resize the nav tabs).
  useEffect(() => {
    const handleResize = () => {
      updateBubblePosition();
    };

    window.addEventListener('resize', handleResize);
    const resizeObserver = new ResizeObserver(handleResize);
    if (navContainerRef.current) resizeObserver.observe(navContainerRef.current);
    const activeButton = activeIndex >= 0 ? buttonRefs.current[activeIndex] : null;
    if (activeButton) resizeObserver.observe(activeButton);

    return () => {
      window.removeEventListener('resize', handleResize);
      resizeObserver.disconnect();
    };
  }, [activeIndex, updateBubblePosition]);

  // Calculate scroll progress for blur effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const progress = Math.min(scrollY / 100, 1);
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavigation = (path: string) => {
    setDrawerOpen(false);
    navigate(pubPath(path));
  };

  const handleDrawerToggle = useCallback(() => {
    setDrawerOpen(!drawerOpen);
  }, [drawerOpen]);

  const handleLogoutClick = () => {
    setDrawerOpen(false);
    setLogoutModalOpen(true);
  };

  const handleLogoutConfirm = async () => {
    setLogoutModalOpen(false);
    try {
      await logout();
    } catch (error) {
      console.error('Error logging out:', error);
      window.location.href = pubPath('/');
    }
  };

  // Hide navigation buttons on story screen
  const isStoryScreen = location.pathname.startsWith('/story/') || location.pathname === '/story';
  const isCreateStoryPage = location.pathname === '/create';

  // Don't render header on story pages
  if (isStoryScreen) {
    return null;
  }

  if (isDashboardPage) {
    return <DashboardHeader scrollProgress={scrollProgress} />;
  }

  // Dynamic styles based on scroll
  const blurAmount = 22 + scrollProgress * 6;
  const bgOpacity = 0.5 + scrollProgress * 0.16;
  const publicHeaderGlassSx = {
    background: `linear-gradient(110deg, rgba(22, 23, 34, ${bgOpacity + 0.08}) 0%, rgba(12, 13, 22, ${bgOpacity}) 100%)`,
    backdropFilter: `blur(${blurAmount}px) saturate(130%)`,
    WebkitBackdropFilter: `blur(${blurAmount}px) saturate(130%)`,
    transition: 'background 0.2s ease, border-bottom 0.2s ease, backdrop-filter 0.2s ease, box-shadow 0.2s ease',
  } as const;

  return (
    <>
      <Box
        component="header"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          ...publicHeaderGlassSx,
          height: 64,
          boxShadow: `0 10px 34px rgba(0, 0, 0, ${0.16 + scrollProgress * 0.12})`,
          borderBottom: '1px solid rgba(255,255,255,.075)',
          paddingInlineStart: 0,
          paddingInlineEnd: 0,
          '&:has(#public-header-breadcrumbs-bar-slot:not(:empty))': {
            borderBottom: 'none',
          },
        }}
      >
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          minHeight: 64,
          boxSizing: 'border-box',
          width: '100%',
          maxWidth: 'none',
          mx: 0,
          position: 'relative',
        }}>
          <Box sx={{
            minWidth: 0,
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
            minHeight: 64,
            borderRadius: 0,
            border: 'none',
            background: 'transparent',
            boxShadow: 'none',
            backdropFilter: 'none',
            WebkitBackdropFilter: 'none',
            overflow: 'hidden',
          }}>
            {/* Logo/Brand - Left */}
            <ButtonBase
              aria-label={pubText("Fable home")}
              onClick={() => navigate(pubPath('/'))}
              sx={{
                display: isCreateStoryPage ? 'none' : 'flex',
                alignItems: 'center',
                alignSelf: 'stretch',
                gap: { xs: 1, md: 1.1 },
                flexShrink: 0,
                px: { xs: 1.25, sm: 1.6, md: 1.75 },
                borderRadius: 0,
                position: 'relative',
                background: 'transparent',
                transition: 'background .3s ease, transform .25s ease',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  right: 0,
                  top: '20%',
                  bottom: '20%',
                  width: '1px',
                  background: 'linear-gradient(180deg, transparent, rgba(216,163,99,.42), transparent)',
                },
                '&:hover': {
                  background: 'rgba(216,163,99,.055)',
                },
                '&:active': {
                  transform: 'scale(.985)',
                },
              }}
            >
              <Box
                sx={{
                  width: { xs: 40, md: 42 },
                  height: { xs: 40, md: 42 },
                  position: 'relative',
                  flexShrink: 0,
                  borderRadius: '50%',
                  boxSizing: 'border-box',
                  p: '2px',
                  boxShadow: isBrandActive
                    ? {
                        xs: 'none',
                        md: '0 0 22px rgba(216,163,99,.48), inset 0 0 10px rgba(255,190,99,.14)',
                      }
                    : '0 0 14px rgba(232,165,76,.2)',
                  transition: 'box-shadow .3s ease, transform .3s ease',
                }}
              >
                <Box
                  component="img"
                  src={fableIcon}
                  alt={pubText("")}
                  aria-hidden
                  sx={{
                    display: 'block',
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    objectFit: 'contain',
                    objectPosition: 'center',
                  }}
                />
                <Box
                  component="svg"
                  viewBox="0 0 42 42"
                  aria-hidden
                  focusable="false"
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none',
                    color: isBrandActive ? 'rgba(244,201,133,.88)' : 'transparent',
                    transition: 'color .3s ease',
                  }}
                >
                  <circle cx="21" cy="21" r="20" fill="none" stroke="currentColor" strokeWidth="1.5" />
                </Box>
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 900,
                  color: '#fff',
                  fontSize: { xs: '1.42rem', md: '1.48rem' },
                  lineHeight: 1,
                  letterSpacing: '-.025em',
                }}
              >{pubText(" fable ")}</Typography>
            </ButtonBase>

            {/* Mobile: hamburger menu */}
            {pubText(isMobile ? (
              <IconButton
                onClick={handleDrawerToggle}
                aria-label={pubText("Open navigation")}
                sx={{
                  color: '#fff',
                  marginInlineStart: 'auto',
                  marginInlineEnd: 0.75,
                  width: 44,
                  height: 44,
                  border: 'none',
                  bgcolor: 'transparent',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.06)',
                  },
                }}
              >
                <MenuIcon sx={{ fontSize: 32 }} />
              </IconButton>
            ) : (
              <>
              {/* Desktop Navigation - full-width tabs with animated bubble */}
              <Box
                ref={navContainerRef}
                sx={{
                  display: 'flex',
                  gap: 0,
                  alignItems: 'center',
                  alignSelf: 'stretch',
                  position: 'relative',
                  flex: '1 1 auto',
                  minWidth: 0,
                  whiteSpace: 'nowrap',
                  px: { lg: 0.45, xl: 0.8 },
                }}
              >
                {/* Animated sliding bubble indicator */}
                {pubText(activeItem && bubbleStyle.width > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '8px',
                      bottom: '8px',
                      left: `${bubbleStyle.left}px`,
                      width: `${bubbleStyle.width}px`,
                      boxSizing: 'border-box',
                      borderRadius: '16px',
                      background: '#1B1E28',
                      border: `2px solid ${activeItem.activeColor}`,
                      boxShadow: 'inset 0 1px 0 rgba(255,255,255,.06)',
                      transition: 'left 0.5s cubic-bezier(0.34, 1.56, 0.64, 1), width 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), background 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease',
                      pointerEvents: 'none',
                      zIndex: 0,
                      '@keyframes bubblePulse': {
                        '0%, 100%': {
                          boxShadow: 'inset 0 1px 0 rgba(255,255,255,.06)',
                        },
                        '50%': {
                          boxShadow: 'inset 0 1px 0 rgba(255,255,255,.10)',
                        },
                      },
                      [`@keyframes bounceSettle${bounceKey}`]: {
                        '0%': { transform: 'scaleX(1.15) scaleY(0.88)' },
                        '25%': { transform: 'scaleX(0.9) scaleY(1.1)' },
                        '45%': { transform: 'scaleX(1.06) scaleY(0.95)' },
                        '65%': { transform: 'scaleX(0.97) scaleY(1.03)' },
                        '80%': { transform: 'scaleX(1.01) scaleY(0.99)' },
                        '100%': { transform: 'scaleX(1) scaleY(1)' },
                      },
                      animation: `bubblePulse 3s ease-in-out infinite, bounceSettle${bounceKey} 0.6s cubic-bezier(0.22, 1, 0.36, 1)`,
                    }}
                  />
                ))}

                {pubText(currentNavItems.map((item, index) => {
                  const active = isActive(item.path);
                  const itemColor = active ? item.activeColor : INACTIVE_COLOR;
                  return (
                    <Button
                      key={item.label}
                      component="a"
                      href={pubPath(item.path)}
                      ref={(el) => { buttonRefs.current[index] = el; }}
                      onClick={(event) => { if (!event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && event.button === 0) { event.preventDefault(); handleNavigation(item.path); } }}
                      sx={{
                        color: itemColor,
                        textTransform: 'none',
                        fontWeight: active ? 800 : 600,
                        fontSize: 'clamp(.68rem, .79vw, .9rem)',
                        letterSpacing: active ? '.005em' : 0,
                        px: { lg: 0.35, xl: 0.75 },
                        py: 0.9,
                        height: 48,
                        minHeight: 48,
                        borderRadius: '16px !important',
                        overflow: 'hidden',
                        minWidth: 0,
                        flex: '1 1 0',
                        whiteSpace: 'nowrap',
                        position: 'relative',
                        zIndex: 1,
                        textShadow: active ? `0 0 14px ${item.activeColor}88` : 'none',
                        transition: 'color 0.3s ease, text-shadow 0.3s ease, transform 0.2s ease',
                        background: 'transparent',
                        '&:hover': {
                          color: '#fff',
                          background: active ? 'transparent' : 'rgba(255,255,255,0.08)',
                          borderRadius: '16px',
                          transform: 'translateY(-1px)',
                        },
                        '&:active': {
                          transform: 'scale(0.98)',
                        },
                      }}
                    >
                      {pubText(item.label)}
                    </Button>
                  );
                }))}
              </Box>

              <Box
                aria-hidden="true"
                sx={{
                  width: '1px',
                  height: 34,
                  flexShrink: 0,
                  background: 'linear-gradient(180deg, transparent, rgba(216,163,99,.46), transparent)',
                }}
              />

              {/* Right side: authentication and app actions */}
              <Box
                sx={{
                  display: 'flex',
                  gap: { lg: 0.7, xl: 1 },
                  alignItems: 'center',
                  flexShrink: 0,
                  paddingInlineStart: { lg: 1, xl: 1.35 },
                  paddingInlineEnd: { lg: 1, xl: 1.35 },
                }}
              >
                {/* Login button - only for non-authenticated users */}
                {pubText(!user.userId && <LoginButton />)}

                {/* Back to Home button - for authenticated users on dashboard pages */}
                {pubText(user.userId && isDashboardPage && (
                  <Button
                    onClick={() => navigate(pubPath('/'))}
                    sx={{
                      background: 'rgba(255,255,255,0.1)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      color: '#fff',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      px: 2.5,
                      py: 1,
                      borderRadius: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: 'rgba(255,255,255,0.15)',
                        transform: 'translateY(-2px)',
                      }
                    }}
                  >{pubRich(" __SLOT_0__ Back to Home ", [<HomeIcon sx={{ fontSize: '1.1rem' }} />])}</Button>
                ))}

                {/* Open Fable button - for authenticated users on public pages */}
                {pubText(user.userId && !isDashboardPage && (
                  <Button
                    onClick={() => navigate(pubPath('/my-stories'))}
                    sx={{
                      background: 'linear-gradient(135deg, #D8A363 0%, #E8A54C 50%, #FF9F32 100%)',
                      color: '#1a1625',
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: { lg: '0.82rem', xl: '0.92rem' },
                      minHeight: 44,
                      px: { lg: 1.6, xl: 2.25 },
                      py: 1.1,
                      borderRadius: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      transition: 'all 0.3s ease',
                      border: '1px solid rgba(255,194,105,.48)',
                      boxShadow: '0 6px 20px rgba(216,163,99,.24), inset 0 1px 0 rgba(255,255,255,.3)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #E8A54C 0%, #FF9F32 50%, #FFB347 100%)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 9px 26px rgba(216,163,99,.42), inset 0 1px 0 rgba(255,255,255,.32)',
                      }
                    }}
                  >{pubRich(
                      " __SLOT_0__ Open fable ",
                      [<AutoStoriesIcon sx={{ fontSize: '1.2rem' }} />]
                    )}</Button>
                ))}

                {/* Tokens - only show on dashboard pages */}
                {pubText(user.userId && isDashboardPage && (
                  <Box
                    onClick={() => navigate(pubPath('/buy-tokens'))}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      cursor: 'pointer',
                      borderRadius: '24px',
                      px: 1.5,
                      py: 0.75,
                      background: 'rgba(216, 163, 99, 0.15)',
                      border: '1px solid rgba(216, 163, 99, 0.3)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        background: 'rgba(216, 163, 99, 0.25)',
                        borderColor: 'rgba(216, 163, 99, 0.5)',
                      }
                    }}
                  >
                    <img
                      src={coinImage}
                      alt={pubText("Coin")}
                      style={{ width: 24, height: 24 }}
                    />
                    <Typography variant="body2" sx={{ color: '#D8A363', fontSize: '0.95rem', fontWeight: 'bold' }}>
                      {pubText(user.tokens)}
                    </Typography>
                  </Box>
                ))}

                {/* Sign Out button - for all authenticated users */}
                {pubText(user.userId && (
                  <IconButton
                    onClick={handleLogoutClick}
                    aria-label={pubText("Sign out")}
                    title={pubText("Sign out")}
                    sx={{
                      width: 44,
                      height: 44,
                      color: 'rgba(255,255,255,0.7)',
                      transition: 'all 0.3s ease',
                      borderRadius: '15px',
                      border: '1px solid rgba(255,255,255,.13)',
                      background: 'linear-gradient(135deg, rgba(255,255,255,.055), rgba(255,255,255,.018))',
                      '&:hover': {
                        color: '#ef4444',
                        background: 'rgba(239, 68, 68, 0.1)',
                        borderColor: 'rgba(239,68,68,.38)',
                        transform: 'translateY(-1px)',
                      }
                    }}
                  >
                    <LogoutIcon />
                  </IconButton>
                ))}
              </Box>
              </>
            ))}
          </Box>
        </Box>

        <Box
          id="public-header-breadcrumbs-bar-slot"
          sx={{
            position: 'absolute',
            top: 64,
            left: 0,
            right: 0,
            height: 44,
            px: { xs: 2, sm: 3, md: 4 },
            display: 'none',
            alignItems: 'center',
            overflow: 'hidden',
            background: 'transparent',
            '&:not(:empty)': {
              display: 'flex',
            },
          }}
        />
      </Box>
      {/* Sign Out Confirmation Modal */}
      <AppDialog
        open={logoutModalOpen}
        onClose={() => setLogoutModalOpen(false)}
        title={pubText("Sign out?")}
        description={pubText("Are you sure you want to sign out?")}
        actions={[
          <ModalButton key="cancel" tone="secondary" onClick={() => setLogoutModalOpen(false)}>{pubText("Cancel")}</ModalButton>,
          <ModalButton key="sign-out" onClick={handleLogoutConfirm}>{pubText("Sign out")}</ModalButton>,
        ]}
      />
      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: false,
          disableScrollLock: true,
        }}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': {
            width: 280,
            background: 'linear-gradient(135deg, rgba(24, 24, 24, 0.95), rgba(19, 18, 18, 0.95))',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderRadius: '16px 0 0 16px',
            boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.3)',
            borderLeft: '1px solid rgba(216, 163, 99, 0.2)',
          },
        }}
      >
        {/* Drawer Header */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <img src={fableIcon} alt={pubText("Fable")} style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'contain', objectPosition: 'center' }} />
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '1.25rem',
                color: '#fff',
              }}
            >{pubText(" fable ")}</Typography>
          </Box>
          <IconButton onClick={handleDrawerToggle} sx={{ color: '#fff' }}>
            <ChevronRightIcon />
          </IconButton>
        </Box>

        <List sx={{ px: 1, py: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Dashboard pages: Show "Back to Home" button at top */}
          {pubText(isDashboardPage && user.userId && (
            <>
              <ListItem disablePadding sx={{ px: 1, mb: 2 }}>
                <ListItemButton
                  onClick={() => handleNavigation('/')}
                  sx={{
                    borderRadius: '12px',
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    '&:hover': {
                      background: 'rgba(255,255,255,0.1)',
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <HomeIcon sx={{ color: INACTIVE_COLOR }} />
                  </ListItemIcon>
                  <ListItemText primary={pubText("Back to Home")} primaryTypographyProps={{ color: INACTIVE_COLOR }} />
                </ListItemButton>
              </ListItem>
              <Box sx={{ px: 2, mb: 1.5 }}>
                <Box sx={{ height: '1px', backgroundColor: 'rgba(255,255,255,0.1)' }} />
              </Box>
            </>
          ))}

          {/* Public pages: Show Open Fable button for logged-in users */}
          {pubText(!isDashboardPage && user.userId && (
            <>
              <ListItem disablePadding sx={{ px: 1, mb: canShowSubscriptionCta ? 1.5 : 2 }}>
                <ListItemButton
                  onClick={() => handleNavigation('/my-stories')}
                  sx={{
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #D8A363 0%, #E8A54C 50%, #FF9F32 100%)',
                    py: 1.5,
                    transition: 'all 0.3s ease',
                    boxShadow: '0 2px 8px rgba(216, 163, 99, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #E8A54C 0%, #FF9F32 50%, #FFB347 100%)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(216, 163, 99, 0.4)',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        backgroundColor: 'rgba(0,0,0,0.15)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <AutoStoriesIcon sx={{ fontSize: '1.2rem', color: '#1a1625' }} />
                    </Box>
                    <ListItemText
                      primary={pubText("Open fable")}
                      sx={{
                        '& .MuiTypography-root': {
                          fontWeight: 700,
                          color: '#1a1625',
                          fontSize: '1rem',
                        },
                      }}
                    />
                    <ChevronRightIcon sx={{ color: '#1a1625' }} />
                  </Box>
                </ListItemButton>
              </ListItem>
              {/* Premium CTA for authenticated non-subscribers */}
              {pubText(canShowSubscriptionCta && (
                <ListItem disablePadding sx={{ px: 1, mb: 2 }}>
                  <ListItemButton
                    onClick={() => handleNavigation(subscriptionCtaPath)}
                    sx={{
                      borderRadius: '12px',
                      background: 'transparent',
                      border: '1px solid rgba(216, 163, 99, 0.5)',
                      py: 1.5,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: 'rgba(216, 163, 99, 0.1)',
                        borderColor: '#D8A363',
                      }
                    }}
                  >
                    <ListItemText
                      primary={pubText(
                        hasActiveTrialOffer ? `Start ${trialUnlockPrice} Trial` : 'Upgrade to Premium'
                      )}
                      secondary={pubText(
                        hasActiveTrialOffer ? `${formatTrialOfferCountdown(trialOfferRemainingMs)} left` : undefined
                      )}
                      sx={{
                        textAlign: 'center',
                        '& .MuiTypography-root': {
                          fontWeight: 600,
                          color: '#D8A363',
                          fontSize: '1rem',
                        },
                        '& .MuiListItemText-secondary': {
                          color: 'rgba(216, 163, 99, 0.72)',
                          fontSize: '0.78rem',
                          mt: 0.25,
                        },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
              <Box sx={{ px: 2, mb: 1.5 }}>
                <Box sx={{ height: '1px', backgroundColor: 'rgba(255,255,255,0.1)' }} />
              </Box>
            </>
          ))}

          {/* Login Buttons at top for non-authenticated users */}
          {pubText(!user.userId && (
            <>
              <ListItem disablePadding sx={{ px: 1, mb: 2 }}>
                <Box sx={{ width: '100%' }}>
                  <LoginButton inDrawer={true} onDrawerClose={() => setDrawerOpen(false)} />
                </Box>
              </ListItem>
              <Box sx={{ px: 2, mb: 1.5 }}>
                <Box sx={{ height: '1px', backgroundColor: 'rgba(255,255,255,0.1)' }} />
              </Box>
            </>
          ))}

          {/* Navigation Items */}
          {pubText(isDashboardPage && user.userId ? (
            <>
              {/* Dashboard account actions mirror the Settings page sections. */}
              {pubText(dashboardDrawerSections.map((section) => (
                <Box key={section.title} sx={{ mt: 1.5 }}>
                  <Typography
                    sx={{
                      px: 2,
                      mb: 0.75,
                      color: 'rgba(255,255,255,0.72)',
                      fontSize: '0.86rem',
                      fontWeight: 700,
                    }}
                  >
                    {pubText(section.title)}
                  </Typography>
                  {pubText(section.items.map((item) => {
                    const itemActive = Boolean(item.path && (
                      location.pathname === item.path || location.pathname + location.search === item.path
                    ));
                    const itemColor = item.destructive ? '#D25454' : itemActive ? '#76A9E7' : INACTIVE_COLOR;
                    const hoverBg = item.destructive
                      ? 'rgba(210, 84, 84, 0.12)'
                      : itemActive
                        ? 'rgba(118, 169, 231, 0.2)'
                        : 'rgba(255,255,255,0.1)';
                    return (
                      <ListItem key={item.label} disablePadding sx={{ px: 1, mb: 0.5 }}>
                        <ListItemButton
                          onClick={() => {
                            if (item.externalUrl) {
                              window.open(item.externalUrl, '_blank', 'noopener,noreferrer');
                              setDrawerOpen(false);
                              return;
                            }
                            if (item.path) handleNavigation(item.path);
                          }}
                          sx={{
                            borderRadius: '12px',
                            backgroundColor: itemActive ? 'rgba(118, 169, 231, 0.15)' : 'transparent',
                            '&:hover': {
                              backgroundColor: hoverBg,
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36, color: itemColor }}>
                            {pubText(item.icon)}
                          </ListItemIcon>
                          <ListItemText
                            primary={pubText(item.label)}
                            primaryTypographyProps={{
                              fontWeight: itemActive ? 600 : 500,
                              color: itemColor,
                              fontSize: '0.9rem',
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  }))}
                </Box>
              )))}
            </>
          ) : (
            drawerNavItems.map((item) => {
              const active = isActive(item.path);
              return (
                <ListItem key={item.path} disablePadding sx={{ px: 1, mb: 0.5 }}>
                  <ListItemButton
                    component="a"
                    href={pubPath(item.path)}
                    onClick={(event) => { if (!event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && event.button === 0) { event.preventDefault(); handleNavigation(item.path); } }}
                    sx={{
                      borderRadius: '12px',
                      backgroundColor: active ? `${item.activeColor}26` : 'transparent',
                      '&:hover': {
                        backgroundColor: active ? `${item.activeColor}33` : 'rgba(255,255,255,0.1)',
                      }
                    }}
                  >
                    <ListItemText
                      primary={pubText(item.label)}
                      primaryTypographyProps={{
                        fontWeight: active ? 600 : 500,
                        color: active ? item.activeColor : INACTIVE_COLOR,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })
          ))}

          <Box sx={{ flex: 1 }} />

          {/* Bottom section for authenticated users - Sign Out button */}
          {pubText(user.userId && (
            <Box sx={{
              pt: 1.5,
              pb: 'calc(16px + env(safe-area-inset-bottom))',
              px: 1,
              borderTop: '1px solid rgba(255,255,255,0.1)',
            }}>
              <ListItemButton
                onClick={handleLogoutClick}
                sx={{
                  borderRadius: '12px',
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.1)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'rgba(239, 68, 68, 0.1)',
                    borderColor: 'rgba(239, 68, 68, 0.5)',
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <LogoutIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />
                </ListItemIcon>
                <ListItemText
                  primary={pubText("Sign Out")}
                  primaryTypographyProps={{
                    fontWeight: 500,
                    color: 'rgba(255,255,255,0.7)',
                  }}
                />
              </ListItemButton>
            </Box>
          ))}
        </List>
      </Drawer>
    </>
  );
};

export default Header;
